import { useCallback, useEffect, useRef, useState } from 'react';
import { WordPressAPIService } from '../services';
import { APIError, FilterItem, PostsParams, WordPressPost } from '../types';
import { devLog } from '../utils';

interface UseWordPressAPIConfig {
  baseUrl: string;
  autoLoad?: boolean;
  defaultParams?: PostsParams;
}

interface UseWordPressAPIReturn {
  // Data
  posts: WordPressPost[];
  featuredPosts: WordPressPost[];
  tags: FilterItem[];

  // Pagination
  totalPages: number;
  totalPosts: number;

  // Loading states
  loading: boolean;
  loadingFeatured: boolean;
  loadingTags: boolean;

  // Error states
  error: APIError | null;

  // Methods
  loadPosts: (params?: PostsParams) => Promise<void>;
  loadFeaturedPosts: () => Promise<void>;
  loadTags: () => Promise<void>;
  retry: () => Promise<void>;
}

export const useWordPressAPI = (config: UseWordPressAPIConfig): UseWordPressAPIReturn => {
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<WordPressPost[]>([]);
  const [tags, setTags] = useState<FilterItem[]>([]);

  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingFeatured, setLoadingFeatured] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);

  const [error, setError] = useState<APIError | null>(null);

  // Create service instance
  const serviceRef = useRef<WordPressAPIService>();
  const lastParamsRef = useRef<PostsParams>();

  // Initialize service
  useEffect(() => {
    serviceRef.current = new WordPressAPIService(config.baseUrl);
  }, [config.baseUrl]);

  // Load posts
  const loadPosts = useCallback(async (params: PostsParams = {}) => {
    if (!serviceRef.current) return;

    const finalParams = { ...config.defaultParams, ...params };
    lastParamsRef.current = finalParams;

    setLoading(true);
    setError(null);

    try {
      const response = await serviceRef.current.getPosts(finalParams);
      setPosts(response.data);

      // Enhanced type safety for pagination values
      const totalPagesValue =
        typeof response.totalPages === 'string'
          ? parseInt(response.totalPages, 10)
          : Number(response.totalPages);
      const totalPostsValue =
        typeof response.totalPosts === 'string'
          ? parseInt(response.totalPosts, 10)
          : Number(response.totalPosts);

      setTotalPages(isNaN(totalPagesValue) ? 1 : totalPagesValue);
      setTotalPosts(isNaN(totalPostsValue) ? 0 : totalPostsValue);
    } catch (err) {
      devLog.error('❌ useWordPressAPI.loadPosts failed:', err);

      // Enhanced error type checking
      const apiError: APIError =
        err instanceof Error
          ? {
              message: err.message,
              operation: 'loadPosts',
              originalError: err,
              timestamp: new Date(),
            }
          : { message: 'An unknown error occurred', operation: 'loadPosts', timestamp: new Date() };

      setError(apiError);
      setPosts([]);
      setTotalPages(1);
      setTotalPosts(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load featured posts
  const loadFeaturedPosts = useCallback(async () => {
    if (!serviceRef.current) return;

    setLoadingFeatured(true);

    try {
      const posts = await serviceRef.current.getFeaturedPosts();
      setFeaturedPosts(posts);
    } catch (err) {
      // Store error in development, but don't block the main functionality
      if (process.env.NODE_ENV === 'development') {
        devLog.warn('Failed to load featured posts:', err);
      }
      setFeaturedPosts([]);
      // Don't set main error state since featured posts are optional
    } finally {
      setLoadingFeatured(false);
    }
  }, []);

  // Load tags
  const loadTags = useCallback(async () => {
    if (!serviceRef.current) return;

    setLoadingTags(true);

    try {
      const tagsData = await serviceRef.current.getTags();
      setTags(tagsData);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        devLog.warn('Failed to load tags:', err);
      }
      setTags([]);
      // Don't set main error state since tags are optional for filtering
    } finally {
      setLoadingTags(false);
    }
  }, []);

  // Retry last operation
  const retry = useCallback(async () => {
    if (lastParamsRef.current) {
      await loadPosts(lastParamsRef.current);
    }
    await loadFeaturedPosts();
  }, [loadPosts, loadFeaturedPosts]);

  // Auto-load data if configured
  useEffect(() => {
    if (config.autoLoad) {
      loadTags();
      if (config.defaultParams) {
        loadPosts(config.defaultParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.autoLoad]);

  return {
    // Data
    posts,
    featuredPosts,
    tags,

    // Pagination
    totalPages,
    totalPosts,

    // Loading states
    loading,
    loadingFeatured,
    loadingTags,

    // Error state
    error,

    // Methods
    loadPosts,
    loadFeaturedPosts,
    loadTags,
    retry,
  };
};
